// 商品スライダー（_tgtp-block-swiper-products）の初期化。
// - <tgtp-swiper-products> が data-swiper-options の JSON から Swiper を初期化する。
//   Swiper 本体（swiper-bundle）は async で読み込まれるため、window.Swiper が現れるまで待つ。
// - Theme Editor で section が再描画されると要素ごと差し替わるため、connectedCallback で初期化し、
//   disconnectedCallback で Swiper を破棄する（DOMContentLoaded には依存しない）。
// - コレクションタブ・フィルターグループから使う API:
//   swiper（instance）/ productIds / filterSlides(productIds) / refresh()
//
// 仕様: docs/swiper-products-parts-specification.md

// 2 段以上（Swiper の Grid）のとき、Swiper は slidesPerView: 'auto' と複数段を併用できない
// （Swiper 12 の型定義: "slidesPerView: 'auto' is currently not compatible with multirow mode"）。
// そのため Liquid は 'auto' を出さず tgtpFixedSlideWidth を立てる。この要素が、
// 「スライド幅（--tgtp-swiper-products-slide-width の px）で何枚並ぶか」を小数の slidesPerView に換算する。
// slidesPerGroup は 1 のまま（並びは「上段が前半」）。
const FIXED_SLIDE_WIDTH_OPTION = 'tgtpFixedSlideWidth';
const DESKTOP_BREAKPOINT = 481;

const SWIPER_SCRIPT_SELECTOR = 'script[src*="swiper-bundle.min.js"]';

let swiperReadyPromise = null;

// window.Swiper が定義されるまで待つ。読み込み済みなら即座に解決する。
function waitForSwiper() {
  if (window.Swiper) return Promise.resolve();
  swiperReadyPromise ??= new Promise((resolve) => {
    const resolveWhenDefined = () => {
      if (window.Swiper) resolve();
    };
    for (const script of document.querySelectorAll(SWIPER_SCRIPT_SELECTOR)) {
      script.addEventListener('load', resolveWhenDefined, { once: true });
    }
    // script タグが見つからない・すでに読み込み済みの場合の保険
    window.addEventListener('load', resolveWhenDefined, { once: true });
  });
  return swiperReadyPromise;
}

class TgtpSwiperProducts extends HTMLElement {
  #swiper = null;
  #abortController = null;
  #originalSlides = null;
  // 表示する商品 id の集合。null は全件表示
  #visibleProductIds = null;
  // スライド幅（px）から slidesPerView を計算するモードか（Grid のとき）
  #usesFixedSlideWidth = false;
  #isUpdatingSlidesPerView = false;

  get swiper() {
    return this.#swiper;
  }

  // 描画時点のすべてのスライドの商品 id（フィルターで外したスライドも含む）
  get productIds() {
    return (this.#originalSlides ?? []).map((slide) => slide.dataset.productId).filter(Boolean);
  }

  async connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // フィルターで外す前の状態を、順番どおりに 1 度だけ記録する
    this.#originalSlides ??= [...this.querySelectorAll('.swiper-wrapper > .swiper-slide')];

    this.#loadNavigationIcons(signal);

    await waitForSwiper();
    if (signal.aborted || !this.isConnected) return;

    this.#initialize();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#swiper?.destroy(true, true);
    this.#swiper = null;
  }

  #initialize() {
    const container = this.querySelector('.swiper');
    if (!container) return;

    let options;
    try {
      options = JSON.parse(this.dataset.swiperOptions ?? '{}');
    } catch {
      return;
    }

    this.#usesFixedSlideWidth = options[FIXED_SLIDE_WIDTH_OPTION] === true;
    delete options[FIXED_SLIDE_WIDTH_OPTION];
    if (this.#usesFixedSlideWidth) {
      // 初期化前は、現在の画面幅で有効な breakpoint の値から計算する
      const activeBreakpoint = options.breakpoints?.[window.innerWidth >= DESKTOP_BREAKPOINT ? DESKTOP_BREAKPOINT : 0] ?? {};
      options.slidesPerView = this.#calculateSlidesPerView(container.clientWidth, {
        spaceBetween: activeBreakpoint.spaceBetween ?? options.spaceBetween,
        slidesOffsetBefore: activeBreakpoint.slidesOffsetBefore,
        slidesOffsetAfter: activeBreakpoint.slidesOffsetAfter,
      });
    }

    if (options.navigation) {
      options.navigation = {
        nextEl: container.querySelector('.swiper-button-next'),
        prevEl: container.querySelector('.swiper-button-prev'),
      };
    }

    const paginationElement = container.querySelector('.swiper-pagination');
    const scrollbarElement = container.querySelector('.swiper-scrollbar');
    if (paginationElement) {
      options.pagination = { el: paginationElement, clickable: true };
    }
    if (scrollbarElement) {
      options.scrollbar = { el: scrollbarElement, draggable: true };
    }

    // 初期化前に filterSlides が呼ばれていた場合、その絞り込みを反映してから初期化する
    if (this.#visibleProductIds) this.#renderSlides();

    this.#swiper = new window.Swiper(container, options);

    if (this.#usesFixedSlideWidth) {
      // breakpoint が切り替わると間隔・offset が変わり、リサイズで幅が変わるため、枚数を計算し直す
      this.#swiper.on('breakpoint', () => this.#updateSlidesPerView());
      this.#swiper.on('resize', () => this.#updateSlidesPerView());
      this.#updateSlidesPerView();
    }
  }

  // (幅 - 前後の offset + 間隔) / (スライド幅 + 間隔)。小数のまま使い、最小は 1
  #calculateSlidesPerView(containerWidth, { spaceBetween = 0, slidesOffsetBefore = 0, slidesOffsetAfter = 0 }) {
    const slideWidth = Number.parseFloat(
      getComputedStyle(this).getPropertyValue('--tgtp-swiper-products-slide-width'),
    );
    if (!(slideWidth > 0)) return 1;
    const usableWidth = containerWidth - slidesOffsetBefore - slidesOffsetAfter + spaceBetween;
    return Math.max(1, usableWidth / (slideWidth + spaceBetween));
  }

  // 適用中の breakpoint の値（swiper.params）で slidesPerView を更新する。update() 中の再入は避ける
  #updateSlidesPerView() {
    const swiper = this.#swiper;
    if (!swiper || swiper.destroyed || !this.#usesFixedSlideWidth || this.#isUpdatingSlidesPerView) return;
    this.#isUpdatingSlidesPerView = true;
    try {
      const value = this.#calculateSlidesPerView(swiper.el.clientWidth, swiper.params);
      if (swiper.params.slidesPerView !== value) {
        swiper.params.slidesPerView = value;
        swiper.update();
      }
    } finally {
      this.#isUpdatingSlidesPerView = false;
    }
  }

  // 指定した商品 id のスライドだけを残す（null は全件表示）。表示されるスライドの数を返す。
  filterSlides(productIds) {
    this.#visibleProductIds = productIds ? new Set(productIds) : null;

    const swiper = this.#swiper;
    if (!swiper) return this.#matchingSlides().length;

    // loop のときは、Swiper が wrapper 内のスライドを並べ替えている（ブランクのスライドも足す）ため、
    // いったん loop を解除してから並べ直し、作り直す
    const isLoop = Boolean(swiper.params.loop);
    if (isLoop) swiper.loopDestroy();
    const visibleCount = this.#renderSlides();
    if (isLoop) swiper.loopCreate();

    swiper.update();
    if (isLoop) {
      swiper.slideToLoop(0, 0);
    } else {
      swiper.slideTo(0, 0);
    }
    return visibleCount;
  }

  // サイズを計算し直す（非表示のパネルを表示したときに呼ぶ）
  refresh() {
    this.#updateSlidesPerView();
    this.#swiper?.update();
  }

  #matchingSlides() {
    const slides = this.#originalSlides ?? [];
    if (!this.#visibleProductIds) return slides;
    return slides.filter((slide) => this.#visibleProductIds.has(slide.dataset.productId));
  }

  // wrapper のスライドをすべて外し、該当するスライドだけを元の順番で戻す
  #renderSlides() {
    const wrapper = this.querySelector('.swiper-wrapper');
    if (!wrapper) return 0;

    const matchingSlides = this.#matchingSlides();
    for (const slide of this.#originalSlides ?? []) slide.remove();
    // Swiper（Grid / loop）が足したブランクのスライドは、並べ直す前に外す
    for (const blankSlide of wrapper.querySelectorAll('.swiper-slide-blank')) blankSlide.remove();
    wrapper.append(...matchingSlides);
    return matchingSlides.length;
  }

  // ナビゲーション矢印に SVG 画像が設定されているとき、SVG を取得してボタンに埋め込む
  async #loadNavigationIcons(signal) {
    const buttons = this.querySelectorAll('.swiper-button-prev[data-icon-url], .swiper-button-next[data-icon-url]');
    await Promise.allSettled(
      [...buttons].map(async (button) => {
        const url = button.dataset.iconUrl;
        if (!url) return;
        const response = await fetch(url, { signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const svg = (await response.text()).trim();
        if (svg.startsWith('<svg')) button.innerHTML = svg;
      }),
    );
  }
}

if (!customElements.get('tgtp-swiper-products')) {
  customElements.define('tgtp-swiper-products', TgtpSwiperProducts);
}
